import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  FileText,
  Languages,
  Lock,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { Drawer } from '../../components/Drawer';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { api } from '../../lib/api';
import { errorMessage, useToast } from '../../lib/toast';
import type { NotificationTemplate, TemplateLocale } from '../../lib/types';

/**
 * The languages the apps ship, in the order the compiled-in copy uses. `en` is
 * mandatory everywhere: it is what an untranslated device falls back to.
 */
export const LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'tr', label: 'Turkish' },
  { code: 'de', label: 'German' },
  { code: 'id', label: 'Indonesian' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ar', label: 'Arabic' },
  { code: 'fr', label: 'French' },
  { code: 'ms', label: 'Malay' },
  { code: 'hi', label: 'Hindi' },
  { code: 'af', label: 'Afrikaans' },
];

export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<NotificationTemplate[]>('/api/notifications/templates'),
  });
}

/**
 * Whether [value] is a link the apps will open: an absolute `http(s)` URL with
 * a real host. Mirrors the backend's check, so the form says what is wrong
 * before the save does.
 */
export function isWebLink(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      url.hostname.includes('.')
    );
  } catch {
    return false;
  }
}

/** The link a template opens, shown as a compact, clickable line. */
export function TemplateLink({ link }: { link: string }) {
  return (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      title={link}
      className="flex min-w-0 items-center gap-1.5 text-xs text-brand hover:underline"
    >
      <ExternalLink size={12} className="shrink-0" />
      <span className="truncate">{link}</span>
    </a>
  );
}

export function TemplatesTab() {
  const templates = useTemplates();
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-2">
          Reusable copy, translated per language. Sends always pick a template.
        </p>
        <Button
          variant="primary"
          icon={<Plus size={16} />}
          onClick={() => setCreating(true)}
        >
          New template
        </Button>
      </div>

      {templates.isError && <ErrorNote error={templates.error} />}

      {templates.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-20" />
          ))}
        </div>
      ) : templates.data && templates.data.length > 0 ? (
        <ul className="grid gap-3 lg:grid-cols-2">
          {templates.data.map((template) => (
            <li key={template.id}>
              <TemplateCard template={template} onEdit={() => setEditing(template)} />
            </li>
          ))}
        </ul>
      ) : (
        <Card>
          <EmptyState
            icon={<FileText size={28} />}
            title="No templates yet"
            description="Create one to send your first notification."
          />
        </Card>
      )}

      {(creating || editing) && (
        <TemplateEditor
          template={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function TemplateCard({
  template,
  onEdit,
}: {
  template: NotificationTemplate;
  onEdit: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => api.delete<void>(`/api/notifications/templates/${template.id}`),
    onSuccess: () => {
      toast.success('Template deleted');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const english =
    template.locales.find((locale) => locale.language === 'en') ??
    template.locales[0];

  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-ink">{template.name}</h3>
            {template.is_system && (
              <Badge tone="brand" icon={<Lock size={11} />}>
                System
              </Badge>
            )}
          </div>
          <p className="mt-0.5 font-mono text-xs text-ink-3">{template.key}</p>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button variant="ghost" onClick={onEdit} aria-label="Edit template">
            <Pencil size={15} />
          </Button>
          {!template.is_system && (
            <Button
              variant="ghost"
              aria-label="Delete template"
              loading={remove.isPending}
              onClick={() => {
                if (confirm(`Delete "${template.name}"? This cannot be undone.`)) {
                  remove.mutate();
                }
              }}
            >
              <Trash2 size={15} className="text-danger" />
            </Button>
          )}
        </div>
      </div>

      {english && (
        <div className="mx-4 mb-4 rounded-lg border border-line bg-raised px-3 py-2.5">
          <p className="text-sm font-medium text-ink">{english.title}</p>
          <p className="mt-0.5 text-xs text-ink-2">{english.body}</p>
          {template.link && (
            <div className="mt-2">
              <TemplateLink link={template.link} />
            </div>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center gap-1.5 border-t border-line px-4 py-2.5 text-xs text-ink-3">
        <Languages size={13} />
        {template.locales.length} language
        {template.locales.length === 1 ? '' : 's'}
      </div>
    </Card>
  );
}

// ── Editor ──────────────────────────────────────────────────────────────────

interface EditorState {
  key: string;
  name: string;
  description: string;
  link: string;
  locales: TemplateLocale[];
}

function TemplateEditor({
  template,
  onClose,
}: {
  template: NotificationTemplate | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!template;

  const [state, setState] = useState<EditorState>(() => ({
    key: template?.key ?? '',
    name: template?.name ?? '',
    description: template?.description ?? '',
    link: template?.link ?? '',
    locales: template?.locales.length
      ? [...template.locales].sort(byLanguageOrder)
      : [{ language: 'en', title: '', body: '' }],
  }));

  const save = useMutation({
    mutationFn: () => {
      const locales = state.locales.map(({ language, title, body }) => ({
        language,
        title: title.trim(),
        body: body.trim(),
      }));
      const payload = {
        name: state.name.trim(),
        description: state.description.trim() || undefined,
        // `null` rather than omitted, so clearing the field removes the link.
        link: state.link.trim() || null,
        locales,
      };
      return isEdit
        ? api.patch<NotificationTemplate>(
            `/api/notifications/templates/${template.id}`,
            payload,
          )
        : api.post<NotificationTemplate>('/api/notifications/templates', {
            ...payload,
            key: state.key.trim(),
          });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Template updated' : 'Template created');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const patchLocale = (index: number, patch: Partial<TemplateLocale>) => {
    setState((current) => ({
      ...current,
      locales: current.locales.map((locale, i) =>
        i === index ? { ...locale, ...patch } : locale,
      ),
    }));
  };

  const addLocale = (language: string) => {
    setState((current) => ({
      ...current,
      locales: [...current.locales, { language, title: '', body: '' }].sort(
        byLanguageOrder,
      ),
    }));
  };

  const removeLocale = (language: string) => {
    setState((current) => ({
      ...current,
      locales: current.locales.filter((locale) => locale.language !== language),
    }));
  };

  const used = new Set(state.locales.map((locale) => locale.language));
  const available = LANGUAGES.filter((language) => !used.has(language.code));
  const link = state.link.trim();
  const linkInvalid = link !== '' && !isWebLink(link);
  const complete =
    state.name.trim() &&
    (isEdit || /^[a-z0-9_]{3,64}$/.test(state.key.trim())) &&
    !linkInvalid &&
    state.locales.every((locale) => locale.title.trim() && locale.body.trim());

  return (
    <Drawer
      open
      title={isEdit ? `Edit “${template.name}”` : 'New template'}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!complete}
            onClick={() => save.mutate()}
          >
            {isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {!isEdit && (
          <Field
            label="Key"
            hint="Lowercase letters, digits and underscores. Permanent."
          >
            <Input
              value={state.key}
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  key: event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                }))
              }
              placeholder="weekend_promo"
            />
          </Field>
        )}

        <Field label="Name">
          <Input
            value={state.name}
            onChange={(event) =>
              setState((current) => ({ ...current, name: event.target.value }))
            }
            placeholder="Weekend promo"
          />
        </Field>

        <Field label="Description" hint="Optional — a note for whoever sends it next">
          <Input
            value={state.description}
            onChange={(event) =>
              setState((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
          />
        </Field>

        <Field
          label="Link"
          hint={
            linkInvalid
              ? undefined
              : 'Optional — opens in the browser when the notification is tapped, from the push or the in-app inbox'
          }
        >
          <Input
            type="url"
            inputMode="url"
            value={state.link}
            onChange={(event) =>
              setState((current) => ({ ...current, link: event.target.value }))
            }
            placeholder="https://"
            maxLength={1024}
            aria-invalid={linkInvalid}
          />
          {linkInvalid && (
            <span className="block text-xs text-danger">
              Enter a full web address starting with https://
            </span>
          )}
        </Field>

        <div className="space-y-3 border-t border-line pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
              Localizations
            </h3>
            {available.length > 0 && (
              <Select
                value=""
                className="w-auto"
                onChange={(event) => {
                  if (event.target.value) addLocale(event.target.value);
                }}
                aria-label="Add a language"
              >
                <option value="">Add language…</option>
                {available.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.label}
                  </option>
                ))}
              </Select>
            )}
          </div>

          <p className="text-xs text-ink-3">
            Each device gets the copy for its own language; anything untranslated
            falls back to English.
          </p>

          {state.locales.map((locale, index) => (
            <div
              key={locale.language}
              className="space-y-2 rounded-lg border border-line p-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink">
                  {labelFor(locale.language)}
                  {locale.language === 'en' && (
                    <span className="ml-2 text-xs font-normal text-ink-3">
                      fallback
                    </span>
                  )}
                </span>
                {locale.language !== 'en' && (
                  <Button
                    variant="ghost"
                    aria-label={`Remove ${labelFor(locale.language)}`}
                    onClick={() => removeLocale(locale.language)}
                  >
                    <Trash2 size={14} className="text-danger" />
                  </Button>
                )}
              </div>

              <Input
                value={locale.title}
                onChange={(event) =>
                  patchLocale(index, { title: event.target.value })
                }
                placeholder="Title"
                maxLength={255}
              />
              <Textarea
                value={locale.body}
                onChange={(event) =>
                  patchLocale(index, { body: event.target.value })
                }
                placeholder="Body"
                rows={2}
                maxLength={1000}
              />
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

function labelFor(code: string): string {
  return LANGUAGES.find((language) => language.code === code)?.label ?? code;
}

function byLanguageOrder(a: TemplateLocale, b: TemplateLocale): number {
  const order = (code: string) => {
    const index = LANGUAGES.findIndex((language) => language.code === code);
    return index === -1 ? LANGUAGES.length : index;
  };
  return order(a.language) - order(b.language);
}
